import * as React from "react";
import { cn } from "@/lib/utils";
import { SectionLabel } from "./GradientText";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FAQItem {
  name: string;
  acceptedAnswer: {
    text: string;
  };
}

interface FAQProps {
  title: string;
  subtitle?: string;
  items: FAQItem[];
}

/**
 * FAQ (Neo-Brutalist Version)
 *
 * Accordion-based FAQ section with:
 * - Bold bordered accordion items
 * - Hard shadows that change on open
 * - Smooth expand/collapse animations
 * - Entrance animations on scroll
 */
export function FAQ({ title, subtitle, items }: FAQProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="faq"
      className="py-24 lg:py-32 bg-[#FFFBF0] relative"
    >
      {/* Decorative geometric shapes - contained in overflow-hidden wrapper */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={cn(
            "absolute -top-8 -left-8 w-32 h-32",
            "bg-[#00EB90] rounded-3xl",
            "border-3 border-black",
            "rotate-12 opacity-20"
          )}
        />
        <div
          className={cn(
            "absolute top-1/4 -right-12 w-40 h-40",
            "bg-[#FFDC00] rounded-full",
            "border-3 border-black",
            "opacity-20"
          )}
        />
        <div
          className={cn(
            "absolute bottom-20 -left-6 w-24 h-24",
            "bg-[#3F5EF4] rounded-2xl",
            "border-3 border-black",
            "-rotate-12 opacity-15"
          )}
        />
        <div
          className={cn(
            "absolute -bottom-10 right-1/4 w-28 h-28",
            "bg-[#FF73A9] rounded-3xl",
            "border-3 border-black",
            "rotate-6 opacity-20"
          )}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-500 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel color="green" className="mb-6">
            FAQ
          </SectionLabel>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight font-['Epilogue'] text-[#1A1A1A] mb-4">
            {title}
          </h2>

          {subtitle && (
            <p className="text-[#666666] text-lg lg:text-xl font-['Sora'] max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        {/* FAQ Accordion */}
        <div
          className={cn(
            "max-w-3xl mx-auto pb-3",
            "transition-all duration-500 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
          style={{ transitionDelay: isVisible ? "150ms" : "0ms" }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {items.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className={cn(
                  "rounded-2xl overflow-hidden",
                  "bg-white",
                  "border-3 border-black",
                  "!border-b-3",
                  "shadow-[6px_6px_0_0_#000000]",
                  "data-[state=open]:shadow-[6px_6px_0_0_#00EB90]",
                  "data-[state=open]:-translate-x-0.5 data-[state=open]:-translate-y-0.5",
                  "transition-all duration-200"
                )}
              >
                <AccordionTrigger
                  className={cn(
                    "px-6 py-5 text-left hover:no-underline",
                    "text-[#1A1A1A] font-bold font-['Epilogue'] text-lg",
                    "hover:bg-[#FFFBF0]",
                    "[&[data-state=open]>svg]:rotate-180",
                    "[&>svg]:text-[#00EB90] [&>svg]:transition-transform [&>svg]:duration-200",
                    "[&>svg]:w-6 [&>svg]:h-6"
                  )}
                >
                  {item.name}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="text-[#666666] text-base leading-relaxed font-['Sora']">
                    {item.acceptedAnswer.text}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
